import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSelector from '../../components/LanguageSelector';

const ClientLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { clientLogin } = useAuth();
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await clientLogin(email, password);
    
    if (result.success) {
      navigate('/client/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4">
        <LanguageSelector variant="compact" />
      </div>
      
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/" className="text-2xl font-bold text-white tracking-tight">
            AI<span className="text-teal-400">CONNEQTORS</span>
          </a>
          <p className="text-gray-500 mt-2">{t('auth.clientLogin')}</p>
        </div>

        {/* Login Card */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-2xl p-8">
          <h2 className="text-xl font-semibold text-white mb-6">{t('auth.signInToContinue')}</h2>
          
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-gray-300 text-sm mb-2">{t('common.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-teal-400 transition-colors"
                placeholder={t('auth.emailPlaceholder')}
                required
                data-testid="client-login-email"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm mb-2">{t('common.password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 pr-12 text-white placeholder-gray-500 outline-none focus:border-teal-400 transition-colors"
                  placeholder={t('auth.passwordPlaceholder')}
                  required
                  data-testid="client-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-400 hover:bg-teal-500 disabled:bg-teal-400/50 text-black font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="client-login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm">
              Need an account?{' '}
              <span className="text-teal-400">Contact your administrator</span>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-gray-600 text-xs text-center mt-6">
          © 2024 AIConneqtors. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ClientLogin;
